import { normalizePartyName } from '@aline/common';
import { uniqBy } from 'ramda';
import { getIsPendingParty } from 'src/components/parties/pending-party';
import type { PartySubset } from 'src/components/workflow/WorkflowPartiesPopover';

export interface PartiesEditorRow {
  readonly checked: boolean;
  readonly party: PartySubset;
  readonly pending: boolean;
}

export interface PartiesEditorRows {
  /** The visible row whose name matches the search, so typing it again selects instead of creating. */
  readonly exactMatch: PartiesEditorRow | null;
  /** Whether the latest search results describe the current search text. */
  readonly isStale: boolean;
  /** Rows pinned above the search results: parties created this session, then the selection at open. */
  readonly pinnedRows: PartiesEditorRow[];
  readonly resultRows: PartiesEditorRow[];
}

/**
 * Orders the party picker. The selection captured when the picker opened stays
 * pinned at the top for the whole session, so unchecking a party never makes it
 * jump away from the cursor. Parties created this session sit above it, newest
 * first. While the server search for the current text is in flight, the
 * previous results are filtered locally so every keystroke narrows the list.
 */
export function getPartiesEditorRows({
  createdParties,
  pinnedParties,
  resultsSearch,
  search,
  searchResults,
  selectedParties,
}: {
  createdParties: readonly PartySubset[];
  pinnedParties: readonly PartySubset[];
  resultsSearch: string | null;
  search: string;
  searchResults: readonly PartySubset[];
  selectedParties: readonly PartySubset[];
}): PartiesEditorRows {
  const trimmedSearch = search.trim().toLowerCase();
  const isStale = resultsSearch?.trim().toLowerCase() !== trimmedSearch;
  const selectedIds = new Set(selectedParties.map(party => party.id));
  const selectedByKey = new Map(selectedParties.map(party => [getPartyKey(party.name), party]));
  const resultsByKey = new Map(searchResults.map(party => [getPartyKey(party.name), party]));

  /**
   * A pending placeholder comes back from the server with a real id, so follow it
   * by name to its saved version. It can be in this session's creates, or in the
   * selection pinned at open when the picker reopened while a create was in flight.
   */
  function resolvePending(party: PartySubset): PartySubset {
    if (!getIsPendingParty(party)) return party;

    const key = getPartyKey(party.name);
    return selectedByKey.get(key) ?? resultsByKey.get(key) ?? party;
  }

  const topParties = uniqBy(
    party => party.id,
    [...[...createdParties].reverse(), ...pinnedParties].map(resolvePending)
  );
  const topIds = new Set(topParties.map(party => party.id));
  const topKeys = new Set(topParties.map(party => getPartyKey(party.name)));

  function matchesSearch(party: PartySubset) {
    return !trimmedSearch || party.name.toLowerCase().includes(trimmedSearch);
  }

  function toRow(party: PartySubset): PartiesEditorRow {
    return { checked: selectedIds.has(party.id), party, pending: getIsPendingParty(party) };
  }

  const pinnedRows = topParties.filter(matchesSearch).map(toRow);
  const resultRows = searchResults
    .filter(party => !topIds.has(party.id) && !topKeys.has(getPartyKey(party.name)))
    .filter(party => !isStale || matchesSearch(party))
    .map(toRow);
  const searchKey = getPartyKey(search);
  const exactMatch = trimmedSearch
    ? ([...pinnedRows, ...resultRows].find(row => getPartyKey(row.party.name) === searchKey) ??
      null)
    : null;

  return { exactMatch, isStale, pinnedRows, resultRows };
}

/** Matches the server's duplicate check so "Acme, Inc." and "acme inc" are one party. */
function getPartyKey(name: string): string {
  return normalizePartyName(name) ?? name.trim().toLowerCase();
}
